import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
if (TOKEN) mapboxgl.accessToken = TOKEN;

/**
 * A small embeddable map that lets the user click to place a pin.
 * Calls onChange({ lat, lng, locationName }) on every placement.
 */
export default function LocationPicker({ initialLat, initialLng, onChange }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const markerRef    = useRef(null);
  const [coords, setCoords] = useState(
    initialLat && initialLng ? { lat: initialLat, lng: initialLng } : null
  );

  useEffect(() => {
    if (!TOKEN || !containerRef.current) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/dark-v11",
      center: [initialLng ?? 8.54, initialLat ?? 47.37],  // Zürich
      zoom: initialLat ? 13 : 12,
      attributionControl: false,
    });

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;

    // If initial coords exist, place a marker immediately
    if (initialLat && initialLng) {
      placeMarker(map, initialLng, initialLat);
    }

    map.on("click", (e) => {
      const { lng, lat } = e.lngLat;
      placeMarker(map, lng, lat);
      const rounded = { lat: +lat.toFixed(7), lng: +lng.toFixed(7) };
      setCoords(rounded);
      onChange({ lat: rounded.lat, lng: rounded.lng, locationName: undefined });
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function placeMarker(map, lng, lat) {
    if (markerRef.current) markerRef.current.remove();

    const el = document.createElement("div");
    el.style.cssText = `
      width: 20px; height: 20px; border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      background: #4488ff;
      border: 2px solid #ffffff;
      box-shadow: 0 0 10px #4488ff88, 0 0 20px #4488ff44;
    `;

    markerRef.current = new mapboxgl.Marker({ element: el, anchor: "bottom" })
      .setLngLat([lng, lat])
      .addTo(map);
  }

  if (!TOKEN) {
    return (
      <div
        style={{
          height: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--color-abyss)",
          border: "1px solid var(--color-border)",
          borderRadius: 8,
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          color: "var(--color-muted)",
          letterSpacing: "0.1em",
        }}
      >
        MAP UNAVAILABLE — set VITE_MAPBOX_TOKEN
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {/* Map container */}
      <div
        ref={containerRef}
        style={{
          height: 220,
          borderRadius: 8,
          overflow: "hidden",
          border: "1px solid var(--color-border)",
          cursor: "crosshair",
        }}
      />

      {/* Instruction / coords overlay */}
      <div
        style={{
          position: "absolute",
          bottom: 8,
          left: 8,
          background: "rgba(10,10,15,0.88)",
          border: "1px solid var(--color-border)",
          borderRadius: 6,
          padding: "5px 10px",
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          pointerEvents: "none",
          backdropFilter: "blur(6px)",
        }}
      >
        {coords ? (
          <span style={{ color: "var(--color-blue)" }}>
            {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
          </span>
        ) : (
          <span style={{ color: "var(--color-muted)" }}>
            Click to place device
          </span>
        )}
      </div>
    </div>
  );
}
